import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import Combobox from './Combobox';
import LoadingSkeleton from '../LoadingSkeleton';

/**
 * A simple wrapper component if you just want to load a list of all the (non-deleted)
 * items a user can see in a table. Also handles hasura enum tables
 */
function ComboboxSelfLoading({
  fieldHasuraName = 'name', // name of the column in hasura
  fieldTableName, // name of the table in hasura
  isEnumTable = false,
  context = {},
  label,
  ...props
}) {
  // What are we showing people?
  const displayName = label || fieldHasuraName;

  // Set up a dynamic query
  const tableQuery = gql`
    query ComboboxSelfLoading_${fieldTableName} {
        ${fieldTableName}(where: { deleted_at: {_is_null: true}}) {
            id
            ${fieldHasuraName}
        }
    }
    `;

  const enumQuery = gql`
    query ComboboxSelfLoading_Enum_${fieldTableName} {
        ${fieldTableName} {
            value
            description
        }
    }
    `;

  // get us the initial values to display
  const { data, loading, error } = useQuery(isEnumTable ? enumQuery : tableQuery, {
    context,
  });
  if (error) {
    console.error(error);
    throw error;
  }

  // extract value from data if it applies
  const items = useMemo(
    () =>
      isEnumTable
        ? data?.[fieldTableName]?.map((x) => ({ ...x, name: x.value }))
        : data?.[fieldTableName]?.map((x) => ({ id: x.id, name: x[fieldHasuraName] })),
    [data],
  );

  if (loading) {
    return <LoadingSkeleton />;
  }

  return <Combobox label={displayName} loading={loading} items={items || []} {...props} />;
}
ComboboxSelfLoading.propTypes = {
  fieldHasuraName: PropTypes.string,
  fieldTableName: PropTypes.string.isRequired,
  isEnumTable: PropTypes.bool,
  label: PropTypes.string,
};
export default ComboboxSelfLoading;
